import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle2, MessageCircle, Copy, ArrowRight, ShoppingBag } from 'lucide-react';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { buildWhatsAppMessage, buildWhatsAppUrl } from '../lib/whatsapp';
import { formatCurrency } from '../lib/utils';
import { Restaurant, CartItem } from '../lib/types';
import { useLoyaltySummary } from '../lib/loyalty-hooks';
import { Sparkles, Gift } from 'lucide-react';
import { useLanguage } from '../lib/i18n';
interface OrderState {
  restaurant: Restaurant;
  orderNumber: string;
  trackingToken: string;
  customerName: string;
  customerPhone: string;
  deliveryType: string;
  paymentMethod?: string;
  deliveryAddress?: string | null;
  mapsUrl?: string | null;
  notes?: string | null;
  ticketImageUrl?: string | null;
  items: CartItem[];
  subtotal: number;
  deliveryFee: number;
  total: number;
  customerTrackingVisible?: boolean;
}
export function OrderConfirmationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as OrderState | null;
  const [copied, setCopied] = useState(false);
  const {
    t
  } = useLanguage();
  if (!state) {
    return <div className="min-h-screen flex flex-col bg-brand-offwhite" data-id="element-4377">
        <MarketplaceHeader data-id="element-4378" />
        <main className="flex-1 flex items-center justify-center py-12" data-id="element-4379">
          <EmptyState icon={ShoppingBag} title={t('confirm_no_order')} description="" actionLabel={t('confirm_back')} onAction={() => navigate('/')} data-id="element-4380" />
        </main>
        <Footer data-id="element-4381" />
      </div>;
  }
  const {
    restaurant,
    orderNumber,
    trackingToken,
    items,
    subtotal,
    deliveryFee,
    total
  } = state;
  const showTracking = state.customerTrackingVisible !== false;
  const {
    summary
  } = useLoyaltySummary(state.customerPhone, restaurant.id);
  const handleCopy = async () => {
    if (!restaurant.whatsapp_number) return;
    const message = buildWhatsAppMessage({
      restaurantName: restaurant.name,
      whatsappNumber: restaurant.whatsapp_number,
      currencySymbol: restaurant.currency_symbol,
      orderNumber: state.orderNumber,
      customerName: state.customerName,
      customerPhone: state.customerPhone,
      deliveryType: state.deliveryType,
      paymentMethod: state.paymentMethod,
      items: state.items,
      subtotal: state.subtotal,
      deliveryFee: state.deliveryFee,
      total: state.total,
      deliveryAddress: state.deliveryAddress,
      mapsUrl: state.mapsUrl,
      notes: state.notes,
      ticketImageUrl: state.ticketImageUrl,
      trackingToken: state.trackingToken,
      customerTrackingVisible: showTracking
    });
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text', err);
    }
  };
  const handleWhatsApp = () => {
    if (!restaurant.whatsapp_number) return;
    const message = buildWhatsAppMessage({
      restaurantName: restaurant.name,
      whatsappNumber: restaurant.whatsapp_number,
      currencySymbol: restaurant.currency_symbol,
      orderNumber: state.orderNumber,
      customerName: state.customerName,
      customerPhone: state.customerPhone,
      deliveryType: state.deliveryType,
      paymentMethod: state.paymentMethod,
      items: state.items,
      subtotal: state.subtotal,
      deliveryFee: state.deliveryFee,
      total: state.total,
      deliveryAddress: state.deliveryAddress,
      mapsUrl: state.mapsUrl,
      notes: state.notes,
      ticketImageUrl: state.ticketImageUrl,
      trackingToken: state.trackingToken,
      customerTrackingVisible: showTracking
    });
    const url = buildWhatsAppUrl(restaurant.whatsapp_number, message);
    window.open(url, '_blank');
  };
  const trackingUrl = `https://track.${restaurant.domain || 'restaurant.com'}/${trackingToken}`;
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(trackingUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy tracking link', err);
    }
  };
  return <div className="min-h-screen flex flex-col bg-brand-offwhite" data-id="element-4382">
      <MarketplaceHeader data-id="element-4383" />

      <main className="flex-1 flex items-center justify-center p-4 py-12" data-id="element-4384">
        <div className="max-w-2xl mx-auto" data-id="element-4385">
          {/* Success Header */}
          <div className="text-center mb-12" data-id="element-4386">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6" data-id="element-4387">
              <CheckCircle2 className="w-10 h-10 text-green-600" data-id="element-4388" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-3" data-id="element-4389">
              {t('confirm_title')}
            </h1>
            <p className="text-xl text-gray-600" data-id="element-4390">{t('confirm_subtitle')}</p>
          </div>

          {/* Order Number Card */}
          <Card className="p-8 mb-8 text-center border-2 border-brand-primary/20 bg-brand-primary/5" data-id="element-4391">
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-4392">
              {t('confirm_order_number')}
            </p>
            <p className="text-5xl font-black text-brand-primary tracking-tight" data-id="element-4393">
              #{orderNumber}
            </p>
          </Card>

          {/* Actions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12" data-id="element-4394">
            <Button onClick={() => navigate(`/track/${trackingToken}`)} className="h-14 text-lg bg-gray-900 hover:bg-gray-800 text-white" data-id="element-4395">
              <ShoppingBag className="w-5 h-5 mr-2" data-id="element-4396" />
              {t('confirm_track')}
            </Button>

            <Button onClick={() => window.open(whatsappUrl, '_blank')} variant="outline" className="h-14 text-lg border-2 hover:bg-green-50 hover:text-green-600 hover:border-green-200" data-id="element-4397">
              <MessageCircle className="w-5 h-5 mr-2 text-green-600" data-id="element-4398" />
              {t('confirm_whatsapp')}
            </Button>
          </div>

          {/* Tracking Link */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm mb-8" data-id="element-4399">
            <p className="text-sm font-medium text-gray-900 mb-4" data-id="element-4400">
              {t('copy_tracking')}
            </p>
            <div className="flex items-center gap-3" data-id="element-4401">
              <div className="flex-1 bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-500 truncate border border-gray-100" data-id="element-4402">
                {trackingUrl}
              </div>
              <Button variant="outline" onClick={handleCopyLink} className="shrink-0" data-id="element-4403">
                {copied ? <span className="text-green-600 flex items-center" data-id="element-4404">
                    <CheckCircle2 className="w-4 h-4 mr-2" data-id="element-4405" />
                    {t('confirm_copied')}
                  </span> : <>
                    <Copy className="w-4 h-4 mr-2" data-id="element-4406" />
                    {t('confirm_copy')}
                  </>}
              </Button>
            </div>
          </div>

          {/* Loyalty Summary Block */}
          {summary && <div className="bg-gradient-to-r from-[#1A1A1A] to-[#2A2A2A] rounded-2xl p-6 text-white shadow-lg border border-[#333]" data-id="element-4407">
              <div className="flex items-center gap-3 mb-4" data-id="element-4408">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center shrink-0" data-id="element-4409">
                  <Sparkles className="w-5 h-5 text-[#F5E9DA]" data-id="element-4410" />
                </div>
                <div data-id="element-4411">
                  <h3 className="font-bold text-lg" data-id="element-4412">¡Ganaste puntos!</h3>
                  <p className="text-sm text-white/70" data-id="element-4413">
                    Tu nivel actual: {summary.current_level || 'Inicial'}
                  </p>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-4 border border-white/10" data-id="element-4414">
                <p className="text-sm font-medium" data-id="element-4415">
                  Tienes un total de{' '}
                  <span className="font-bold text-[#F5E9DA]" data-id="element-4416">
                    {summary.current_points} puntos
                  </span>{' '}
                  acumulados.
                </p>
              </div>

              {summary.available_rewards_count > 0 && <div className="mt-4 flex items-start gap-3 bg-[#006847]/20 border border-[#006847]/30 rounded-xl p-4" data-id="element-4417">
                  <Gift className="w-5 h-5 text-[#006847] shrink-0 mt-0.5" data-id="element-4418" />
                  <p className="text-sm font-medium text-white" data-id="element-4419">
                    ¡Tienes {summary.available_rewards_count}{' '}
                    {summary.available_rewards_count === 1 ? 'beneficio disponible' : 'beneficios disponibles'}{' '}
                    para tu próxima compra!
                  </p>
                </div>}
            </div>}

          {/* Order Details */}
          <Card className="p-6" data-id="element-4420">
            <h3 className="font-bold text-gray-900 mb-4" data-id="element-4421">
              {t('order_summary')}
            </h3>
            <div className="space-y-4" data-id="element-4422">
              <div className="flex justify-between" data-id="element-4423">
                <span className="text-gray-600" data-id="element-4424">
                  {t('confirm_order_number')}
                </span>
                <span className="font-semibold text-gray-900" data-id="element-4425">
                  {orderNumber}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-4426">
                <span className="text-gray-600" data-id="element-4427">{t('total')}</span>
                <span className="font-semibold text-gray-900" data-id="element-4428">
                  {formatCurrency(total)}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-4429">
                <span className="text-gray-600" data-id="element-4430">{t('checkout_payment')}</span>
                <span className="text-gray-600" data-id="element-4431">
                  {state.paymentMethod || 'No especificado'}
                </span>
              </div>
              {state.deliveryAddress && <div className="flex justify-between" data-id="element-4432">
                  <span className="text-gray-600" data-id="element-4433">{t('checkout_address')}</span>
                  <span className="text-gray-600" data-id="element-4434">{state.deliveryAddress}</span>
                </div>}
            </div>
          </Card>

          <div className="space-y-4" data-id="element-4435">
            {restaurant.whatsapp_number && <>
                <Button size="lg" fullWidth onClick={handleWhatsApp} className="bg-[#25D366] hover:bg-[#20bd5a] text-white border-none shadow-lg shadow-emerald-200 h-14 text-lg" data-id="element-4436">
                  <MessageCircle className="w-6 h-6 mr-2" data-id="element-4437" />
                  {t('confirm_whatsapp')}
                </Button>

                <Button variant="secondary" size="lg" fullWidth onClick={handleCopy} className="h-14" data-id="element-4438">
                  <Copy className="w-5 h-5 mr-2" data-id="element-4439" />
                  {copied ? t('confirm_copied') : t('confirm_copy')}
                </Button>
              </>}

            {showTracking && <Button variant="ghost" size="lg" fullWidth onClick={() => navigate(`/track/${trackingToken}`)} className="h-14 border border-gray-200" data-id="element-4440">
                {t('confirm_track')}
                <ArrowRight className="w-5 h-5 ml-2" data-id="element-4441" />
              </Button>}
          </div>
        </div>
      </main>

      <Footer data-id="element-4442" />
    </div>;
}